import React, {useEffect, useState} from "react";
import {collection, getDocs, query, where} from "@firebase/firestore";
import {httpsCallable} from "@firebase/functions";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faBed} from "@fortawesome/free-solid-svg-icons";
import {db, functions} from "../../../firebase/FirebaseProvider";
import {Location, User} from "../../types/Types";
import "./LocationStayButton.css";

interface Props {
    location: Location;
    currentUser: User;
    variant?: "v2" | "legacy";
}

function LocationStayButton(props: Props) {
    const [stayed, setStayed] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const staysSnapshot = await getDocs(query(
                collection(db, "stays"),
                where("userId", "==", props.currentUser.id),
                where("locationId", "==", props.location.id)
            ));
            const activeStay = staysSnapshot.docs.some(stayDoc => stayDoc.data().stayed === true);
            if (!cancelled) {
                setStayed(activeStay);
            }
        })().catch(error => {
            console.error("Failed to load stay status:", error);
        });

        return () => {
            cancelled = true;
        };
    }, [props.currentUser.id, props.location.id]);

    const toggleStay = async () => {
        if (saving) {
            return;
        }

        setSaving(true);
        try {
            const toggleStayFunction = httpsCallable(functions, "toggleStayV2");
            const response = await toggleStayFunction({locationId: props.location.id});
            const result = response.data as {
                status: number;
                code: string;
                stayed?: boolean;
                locationStayCount?: number;
                userStayCount?: number;
            };
            if (result.status === 403) {
                alert(result.code);
                return;
            }
            const nextStayed = result.stayed ?? !stayed;
            setStayed(nextStayed);
            window.dispatchEvent(new CustomEvent("stl:stay-count-changed", {
                detail: {
                    adjustment: nextStayed ? 1 : -1,
                    locationStayCount: result.locationStayCount,
                    userStayCount: result.userStayCount,
                    locationId: props.location.id,
                    userId: props.currentUser.id,
                },
            }));
        } catch (error) {
            console.error("Failed to update stay status:", error);
            alert("Failed to update stay status");
        } finally {
            setSaving(false);
        }
    };

    const className = props.variant === "legacy"
        ? `location-stay-button${stayed ? " location-stay-button-active" : ""}`
        : `location-v2-stay-button${stayed ? " location-v2-stay-button-active" : ""}`;

    return (
        <button
            aria-label={stayed ? "Remove hotel stay" : "Mark hotel as stayed"}
            className={className}
            disabled={saving}
            onClick={toggleStay}
            title={stayed ? "Stayed here" : "Mark as stayed"}
            type="button"
        >
            <FontAwesomeIcon icon={faBed} />
        </button>
    );
}

export default LocationStayButton;
